import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHelmet from '@/components/base/SEOHelmet';

const siteUrl = 'https://nlafv.cn';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-2">分类未找到</h2>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryVideos = videos.filter((v) => v.categoryId === category.id);
  const categoryArticles = articles.filter((a) => a.categoryId === category.id);
  const categoryUrl = `${siteUrl}/category/${category.slug}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: category.name, item: categoryUrl },
    ],
  };

  return (
    <>
      <SEOHelmet
        title={`${category.name} - 高清樱花视频素材`}
        description={category.description}
        keywords={`${category.name},高清樱花视频,樱花视频素材,樱花实拍`}
        canonicalUrl={categoryUrl}
        ogType="website"
        schema={breadcrumbSchema}
      />

      <Navbar />

      <main>
        {/* Category Hero */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={category.image}
            alt={category.name}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-white/70 max-w-xl">{category.description}</p>
            <p className="text-xs text-white/50 mt-3">{category.count} 个视频</p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: category.name },
            ]}
          />
        </div>

        {/* Other Categories */}
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6 pb-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  cat.id === category.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Videos Grid */}
        <section className="max-w-screen-2xl mx-auto px-4 md:px-6 py-8">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
            视频列表 <span className="text-sm font-normal text-foreground-500 ml-2">({categoryVideos.length} 个)</span>
          </h2>
          {categoryVideos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {categoryVideos.map((video) => (
                <div
                  key={video.id}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 cursor-pointer"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                      {video.duration}
                    </div>
                    <div className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-full">
                      {video.resolution}
                    </div>
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 mb-2">
                      {video.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i> {video.views.toLocaleString()}
                      </span>
                      <span>{video.date}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-foreground-500">暂无视频，敬请期待</p>
            </div>
          )}
        </section>

        {/* Related Articles */}
        {categoryArticles.length > 0 && (
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {categoryArticles.slice(0, 3).map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200 transition-colors cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xs px-2.5 py-1 bg-primary-100 text-primary-700 rounded-full font-medium">
                          {article.categoryName}
                        </span>
                        <span className="text-xs text-foreground-500">{article.readTime}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}